import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Heart, ClipboardList, Settings, LogOut, X, Camera, Save, AlertCircle, ShoppingCart, Trash2, Search, ArrowRight, Package, Clock, CheckCircle, Truck, ChevronRight, Shield, Eye, Phone, MapPin, Mail, Facebook, MessageCircle, Instagram } from 'lucide-react';
import { toggleFavoris, ChercherButton } from './slice';
import { patchCompte, getDataCommandes, getDataComptes, getDataClimats, getDataProjets, uploadUserImage, sendAuthCode, verifyAuthCode, getContactSettings, updateContactSettings } from './action';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import '../styles/userProfileModal.css';
import API_BASE_URL from '../config';

export function UserProfileModal({ isOpen, onClose, hideAdminLink }) {
    const [activeTab, setActiveTab] = useState('favoris');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [step, setStep] = useState('form'); // 'form' or 'verify'
    const [verificationCode, setVerificationCode] = useState("");
    const [generatedCode, setGeneratedCode] = useState("");

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const loggedUser = JSON.parse(localStorage.getItem('user'));
    const favorisIds = useSelector(state => state.favoris) || [];
    const allProducts = useSelector(state => state.listC) || [];
    const allOrders = useSelector(state => state.commandes) || [];

    const [name, setName] = useState(loggedUser?.name || "");
    const [image, setImage] = useState(loggedUser?.profilePic ? loggedUser.profilePic.replace('http://localhost:8000', API_BASE_URL.replace('/api', '')) : "");
    const [password, setPassword] = useState("");
    const [status, setStatus] = useState({ type: '', msg: '' });
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    // Contact Settings State (super admin only) — initialize from localStorage cache for instant pre-fill
    const DEFAULT_CONTACT = { localisation: 'Casablanca, Maroc', telephone: '+212 5XX XXXXXX', email: 'contact@jamaslens.ma', facebook: 'https://facebook.com', whatsapp: 'https://wa.me/212675349447', instagram: 'https://instagram.com' };
    const cachedContact = (() => { try { const c = localStorage.getItem('contact_settings'); return c ? JSON.parse(c) : null; } catch(_) { return null; } })();
    const [contactForm, setContactForm] = useState({ ...DEFAULT_CONTACT, ...(cachedContact || {}) });
    const [savedContact, setSavedContact] = useState({ ...DEFAULT_CONTACT, ...(cachedContact || {}) });
    const [contactLoading, setContactLoading] = useState(false);
    const [contactStatus, setContactStatus] = useState({ type: '', msg: '' });
    const [emptyFields,   setEmptyFields]   = useState({ localisation: false, telephone: false, email: false, facebook: false, whatsapp: false, instagram: false });
    const [shakingFields, setShakingFields] = useState({ localisation: false, telephone: false, email: false, facebook: false, whatsapp: false, instagram: false });

    const handleContactBlur = (field) => {
        if (!contactForm[field]?.trim()) {
            setEmptyFields(e  => ({ ...e,  [field]: true  }));
            setShakingFields(s => ({ ...s, [field]: true  }));
        }
    };
    const handleContactChange = (field, value) => {
        setContactForm(f => ({ ...f, [field]: value }));
        if (value.trim()) setEmptyFields(e => ({ ...e, [field]: false }));
    };
    const stopShake = (field) => setShakingFields(s => ({ ...s, [field]: false }));
    const isContactFormValid = Object.keys(contactForm).every(k => contactForm[k]?.trim() !== '');

    const isModified = name !== (loggedUser?.name || "") || image !== (loggedUser?.profilePic || "") || password !== "";

    const handleCancelEdits = () => {
        setName(loggedUser?.name || "");
        setImage(loggedUser?.profilePic || "");
        setPassword("");
        setSelectedFile(null);
        setNewCropBlob(null);
        setStatus({ type: '', msg: '' });
    };

    // Cropper States
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [tempImage, setTempImage] = useState(null);
    const [framePos, setFramePos] = useState({ x: 50, y: 50 });
    const [frameSize, setFrameSize] = useState(150);
    const [isDraggingFrame, setIsDraggingFrame] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageSize, setImageSize] = useState({ w: 0, h: 0 });
    const [displaySize, setDisplaySize] = useState({ w: 300, h: 300 });
    const [selectedFile, setSelectedFile] = useState(null);
    const [newCropBlob, setNewCropBlob] = useState(null);

    useEffect(() => {
        if (isOpen) {
            const userStr = localStorage.getItem('user');
            const user = userStr ? JSON.parse(userStr) : null;
            if (user && (user.role?.toLowerCase() === 'admin' || user.role?.toLowerCase() === 'super admin')) {
                dispatch(getDataCommandes());
            }
            if (user && user.role?.toLowerCase() === 'super admin') {
                dispatch(getContactSettings()).then(res => {
                    if (res.payload) {
                        const fresh = {
                            localisation: res.payload.localisation || DEFAULT_CONTACT.localisation,
                            telephone:    res.payload.telephone    || DEFAULT_CONTACT.telephone,
                            email:        res.payload.email        || DEFAULT_CONTACT.email,
                            facebook:     res.payload.facebook     || DEFAULT_CONTACT.facebook,
                            whatsapp:     res.payload.whatsapp     || DEFAULT_CONTACT.whatsapp,
                            instagram:    res.payload.instagram    || DEFAULT_CONTACT.instagram,
                        };
                        setContactForm(fresh);
                        setSavedContact(fresh);
                        localStorage.setItem('contact_settings', JSON.stringify(fresh));
                    }
                });
            }
            dispatch(getDataComptes());
            dispatch(getDataClimats());
            dispatch(getDataProjets());

            const params = new URLSearchParams(window.location.search);
            if (params.get('openProfile') === 'true') {
                setActiveTab('missions');
            }
        }
    }, [isOpen, dispatch]);

    if (!isOpen) return null;

    const handleLogout = () => {
        setShowLogoutConfirm(true);
    };

    const confirmLogoutAction = () => {
        localStorage.removeItem('user');
        localStorage.removeItem('firebase_token');
        onClose();
        navigate("/login");
        setShowLogoutConfirm(false);
    };

    const handleToggleFavoris = (climId) => {
        const newFavoris = favorisIds.includes(climId)
            ? favorisIds.filter(id => id !== climId)
            : [...favorisIds, climId];
        dispatch(toggleFavoris(climId));
        dispatch(patchCompte({ id: loggedUser.id, favoris: newFavoris }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onload = () => {
                const img = new Image();
                img.src = reader.result;
                img.onload = () => {
                    const ratio = img.width / img.height;
                    let dw, dh;
                    if (ratio > 1) { // Wide image
                        dw = 300;
                        dh = 300 / ratio;
                    } else { // Tall image
                        dh = 300;
                        dw = 300 * ratio;
                    }
                    setImageSize({ w: img.width, h: img.height });
                    setDisplaySize({ w: dw, h: dh });
                    setTempImage(reader.result);
                    setCropModalOpen(true);
                    const initialSize = Math.min(dw, dh, 150);
                    setFrameSize(initialSize);
                    setFramePos({ x: (dw - initialSize) / 2, y: (dh - initialSize) / 2 });
                };
            };
            reader.readAsDataURL(file);
        }
    };

    const handleFrameMouseDown = (e) => {
        if (e.target.className.includes('resize-handle')) {
            setIsResizing(true);
        } else {
            setIsDraggingFrame(true);
        }
        setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleFrameMouseMove = (e) => {
        if (!isDraggingFrame && !isResizing) return;
        
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        
        if (isResizing) {
            const newSize = Math.max(50, Math.min(displaySize.w - framePos.x, displaySize.h - framePos.y, frameSize + dx));
            setFrameSize(newSize);
        } else {
            setFramePos({
                x: Math.max(0, Math.min(displaySize.w - frameSize, framePos.x + dx)),
                y: Math.max(0, Math.min(displaySize.h - frameSize, framePos.y + dy))
            });
        }
        setDragStart({ x: e.clientX, y: e.clientY });
    };

    const stopAction = () => {
        setIsDraggingFrame(false);
        setIsResizing(false);
    };

    const cropImageForPreview = () => {
        const canvas = document.createElement('canvas');
        const img = new Image();
        img.src = tempImage;
        img.onload = () => {
            const size = 300;
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');

            const scaleX = img.width / displaySize.w;
            const scaleY = img.height / displaySize.h;

            const sx = framePos.x * scaleX;
            const sy = framePos.y * scaleY;
            const sw = frameSize * scaleX;
            const sh = frameSize * scaleY;

            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);

            canvas.toBlob((blob) => {
                setNewCropBlob(blob);
                setImage(canvas.toDataURL('image/jpeg')); // Show preview
                setCropModalOpen(false);
            }, 'image/jpeg', 0.9);
        };
    };



    const handleSaveForm = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        setStatus({ type: '', msg: '' });
        
        let profileUpdated = false;
        try {
            // 1. Upload Cropped Image if exists
            let finalImageUrl = image;
            if (newCropBlob) {
                const formData = new FormData();
                formData.append('image', newCropBlob, 'profile.jpg');
                const uploadResult = await dispatch(uploadUserImage({ id: loggedUser.id, formData })).unwrap();
                finalImageUrl = uploadResult.image_user?.startsWith('data:image') ? uploadResult.image_user : `${API_BASE_URL.replace('/api', '')}/storage/photos/${uploadResult.image_user}`;
                profileUpdated = true;
            }

            // Update username if name changed or image changed
            if (name !== loggedUser.name || profileUpdated) {
                const updatedData = { id: loggedUser.id, name, profilePic: finalImageUrl };
                await dispatch(patchCompte(updatedData)).unwrap();
                const newUser = { 
                    ...loggedUser, 
                    name: updatedData.name,
                    profilePic: updatedData.profilePic?.length < 100000 ? updatedData.profilePic : loggedUser.profilePic
                };
                localStorage.setItem('user', JSON.stringify(newUser));
                setImage(finalImageUrl);
                profileUpdated = true;
            }

            // 2. Handle Password Change Initiation
            if (password) {
                await dispatch(sendAuthCode(loggedUser.email)).unwrap();
                setStep('verify');
                setStatus({ 
                    type: 'success', 
                    msg: profileUpdated 
                        ? `Profil mis à jour. Un code a été envoyé à ${loggedUser.email} pour confirmer le nouveau mot de passe.` 
                        : `Un code de vérification a été envoyé à ${loggedUser.email}` 
                });
            } else if (profileUpdated) {
                setStatus({ type: 'success', msg: 'Profil mis à jour !' });
                setNewCropBlob(null);
            }
        } catch (err) {
            const msg = err?.status === 413
                ? "La photo est trop grande. Choisissez une image de moins de 2 MB."
                : err?.message?.includes('image')
                    ? "Impossible de télécharger la photo. Réessayez."
                    : "Mise à jour échouée. Veuillez réessayer.";
            setStatus({ type: 'error', msg });
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        setStatus({ type: '', msg: '' });
        try {
            await dispatch(sendAuthCode(loggedUser.email)).unwrap();
            setStatus({ type: 'success', msg: `Un nouveau code a été envoyé à ${loggedUser.email}` });
        } catch (err) {
            setStatus({ type: 'error', msg: "Impossible d'envoyer le code. Vérifiez votre connexion." });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyPasswordChange = async (e) => {
        if (e) e.preventDefault();
        if (!verificationCode) {
            setStatus({ type: 'error', msg: 'Veuillez entrer le code.' });
            return;
        }
        setLoading(true);
        setStatus({ type: '', msg: '' });
        try {
            await dispatch(verifyAuthCode({
                email: loggedUser.email,
                code: verificationCode,
                new_password: password
            })).unwrap();
            
            setStatus({ type: 'success', msg: 'Mot de passe mis à jour !' });
            setStep('form');
            setPassword("");
            setVerificationCode("");
        } catch (error) {
            setStatus({ type: 'error', msg: "Code invalide ou expiré. Cliquez sur Renvoyer pour en obtenir un nouveau." });
        } finally {
            setLoading(false);
        }
    };

    const userOrders = allOrders.filter(order =>
        order.username_Client === loggedUser.name || order.email_Client === loggedUser.email
    );

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'en attente': return <Clock size={16} color="#f59e0b" />;
            case 'livrée': return <CheckCircle size={16} color="#10b981" />;
            case 'en cours': return <Truck size={16} color="#3b82f6" />;
            default: return <Package size={16} color="#6b7280" />;
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'favoris':
                const favoriteItems = allProducts.filter(item => favorisIds.includes(item.id));
                return (
                    <div className="modal-tab-content">
                        <div className="tab-header">
                            <h2>Mes Favoris</h2>
                        </div>
                        {favoriteItems.length === 0 ? (
                            <div className="empty-state">
                                <Heart size={48} color="#ccc" />
                                <p>Aucun favori pour le moment.</p>
                            </div>
                        ) : (
                            <div className="list-stack-rows">
                                {favoriteItems.map(item => (
                                    <div key={item.id} className="row-item favoris-row">
                                        <div className="row-img-square">
                                            <img src={item.image} alt="" />
                                        </div>
                                        <div className="row-details">
                                            <h4>{item.nomClimatisseur}</h4>
                                            <span className="row-price">{item.tailles[0].prix} DH</span>
                                        </div>
                                        <div className="row-actions">
                                            <button className="btn-row-view" onClick={() => { navigate(`/infoClimatisseur/${item.id}`, { state: { fromList: true } }); onClose(); }}>Voir</button>
                                            <button className="btn-row-del" onClick={() => handleToggleFavoris(item.id)}><Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 'missions':
                return (
                    <div className="modal-tab-content">
                        <div className="tab-header">
                            <h2>Mes Commandes</h2>
                        </div>
                        {userOrders.length === 0 ? (
                            <div className="empty-state">
                                <Package size={48} color="#ccc" />
                                <p>Aucune commande passée.</p>
                            </div>
                        ) : (
                            <div className="list-stack-rows">
                                {userOrders.map(order => (
                                    <div key={order.id} className="row-item order-row">
                                        <div className="order-main-info">
                                            <span className="order-id">#{order.id}</span>
                                        </div>
                                        <div className="row-details">
                                            <div className="order-time-group">
                                                <Clock size={16} color="#f59e0b" fill="#f59e0b10" />
                                                <span className="order-date">{order.date}</span>
                                            </div>
                                            <span className="order-status-lbl">{order.statut}</span>
                                        </div>
                                        <div className="row-amount-info">
                                            <span className="order-amount">{order.total} DH</span>
                                        </div>
                                        <div className="row-actions">
                                            <button className="btn-row-view" onClick={() => setSelectedOrder(order)}>Détails</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            case 'settings':
                return (
                    <div className="modal-tab-content">
                        <div className="tab-header">
                            <h2>Paramètres</h2>
                        </div>
                        <div className="settings-mini-form">
                            <div className="avatar-edit">
                                <div className="avatar-preview-wrapper">
                                    <img src={image || `https://ui-avatars.com/api/?name=${name}&background=700000&color=fff`} alt="" />
                                    <div className="avatar-edit-actions">
                                        <label className="cam-btn"><Camera size={16} /><input type="file" hidden onChange={handleImageChange} accept="image/*" /></label>

                                    </div>
                                </div>
                            </div>
                            <div className="settings-mini-form">
                            <div className="form-row-mini">
                                <div className="form-inline-group">
                                    <label>Nom complet</label>
                                    <input 
                                        type="text" 
                                        value={name} 
                                        onChange={(e) => setName(e.target.value)} 
                                        placeholder="Votre nom"
                                    />
                                </div>
                                <div className="form-inline-group">
                                    <label>Nouveau mot de passe</label>
                                    <input 
                                        type="password" 
                                        value={password} 
                                        onChange={(e) => setPassword(e.target.value)} 
                                        placeholder="Min. 8 caractères"
                                        autoComplete="new-password"
                                    />
                                </div>
                            </div>

                            {step === 'form' ? (
                                <div className="settings-actions-group">
                                    <button 
                                        className="btn-save-mini" 
                                        onClick={handleSaveForm} 
                                        disabled={loading}
                                    >
                                        {loading ? "Chargement..." : "Enregistrer"}
                                    </button>
                                    <button 
                                        className="btn-cancel-mini" 
                                        onClick={handleCancelEdits} 
                                        disabled={!isModified || loading}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            ) : (
                                <div className="verification-section" style={{ marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                                    <div className="verification-row">
                                        <div className="form-inline-group">
                                            <label>Code de vérification</label>
                                            <input 
                                                type="text" 
                                                placeholder="Entrez le code" 
                                                value={verificationCode}
                                                onChange={(e) => setVerificationCode(e.target.value)}
                                            />
                                        </div>
                                        <button className="btn-cancel-mini" onClick={handleResendCode} disabled={loading} style={{ height: '45px', flex: '0 0 120px' }}>
                                            {loading ? "..." : "Renvoyer"}
                                        </button>
                                    </div>
                                    
                                    <div className="verification-actions-row">
                                        <button className="btn-save-mini" onClick={handleVerifyPasswordChange} disabled={loading} style={{ flex: 2 }}>
                                            {loading ? "Validation..." : "Valider"}
                                        </button>
                                        <button 
                                            className="btn-outline-mini" 
                                            onClick={() => setStep('form')} 
                                        >
                                            Annuler
                                        </button>
                                    </div>
                                </div>
                            )}
                            {status.msg && <div className={`alert-mini ${status.type}`} style={{ marginTop: '20px' }}>{status.msg}</div>}
                        </div>
                        </div>
                    </div>
                );
            case 'contact-settings':
                const handleContactSave = async (e) => {
                    e.preventDefault();
                    if (!isContactFormValid) return;
                    setContactLoading(true);
                    setContactStatus({ type: '', msg: '' });
                    try {
                        const res = await dispatch(updateContactSettings(contactForm)).unwrap();
                        if (res.settings) {
                            const saved = { ...DEFAULT_CONTACT, ...res.settings };
                            localStorage.setItem('contact_settings', JSON.stringify(saved));
                            setSavedContact(saved);
                            setContactForm(saved);
                        }
                        setContactStatus({ type: 'success', msg: '✅ Informations de contact mises à jour avec succès.' });
                    } catch (err) {
                        const errMsg = err?.message || err?.data?.message || err?.error || 'Vérifiez votre connexion et réessayez.';
                        setContactStatus({ type: 'error', msg: `❌ Erreur : ${errMsg}` });
                    } finally {
                        setContactLoading(false);
                    }
                };

                const csClass = (field) =>
                    `cs-input${emptyFields[field] ? ' cs-empty' : ''}${shakingFields[field] ? ' cs-shake' : ''}`;

                return (
                    <div className="modal-tab-content">
                        <div className="tab-header">
                            <h2>Paramètres Contact</h2>
                            <p style={{ color: '#888', marginTop: '4px', fontSize: '0.85rem' }}>Ces informations apparaissent dans le footer du site.</p>
                        </div>
                        <form className="contact-settings-form" onSubmit={handleContactSave} noValidate>
                            {[  
                                { field: 'localisation', label: <><MapPin size={14}/> Localisation</>,       type: 'text',  ph: 'ex: Casablanca, Maroc'         },
                                { field: 'telephone',    label: <><Phone size={14}/> Téléphone</>,           type: 'text',  ph: 'ex: +212 6XX XXXXXX'           },
                                { field: 'email',        label: <><Mail size={14}/> Email</>,                type: 'text',  ph: 'ex: contact@jamaslens.ma'      },
                                { field: 'facebook',     label: <><Facebook size={14}/> Lien Facebook</>,   type: 'text',  ph: 'https://facebook.com/...'      },
                                { field: 'whatsapp',     label: <><MessageCircle size={14}/> Lien WhatsApp</>, type: 'text', ph: 'https://wa.me/212...'         },
                                { field: 'instagram',    label: <><Instagram size={14}/> Lien Instagram</>, type: 'text',  ph: 'https://instagram.com/...'     },
                            ].map(({ field, label, type, ph }) => (
                                <div className="cs-field-group" key={field}>
                                    <label>{label}</label>
                                    <input
                                        type={type}
                                        className={csClass(field)}
                                        placeholder={ph}
                                        value={contactForm[field] || ''}
                                        onChange={e  => handleContactChange(field, e.target.value)}
                                        onBlur={()  => handleContactBlur(field)}
                                        onAnimationEnd={() => stopShake(field)}
                                    />
                                    {emptyFields[field] && (
                                        <span className="cs-field-error">Ce champ est obligatoire.</span>
                                    )}
                                </div>
                            ))}
                            {contactStatus.msg && (
                                <div className={`alert-mini ${contactStatus.type}`} style={{ marginTop: '12px' }}>
                                    {contactStatus.msg}
                                </div>
                            )}
                            <div className="settings-actions" style={{ marginTop: '20px' }}>
                                <button
                                    type="submit"
                                    className="btn-save-settings"
                                    disabled={contactLoading || !isContactFormValid}
                                    title={!isContactFormValid ? 'Veuillez remplir tous les champs.' : ''}
                                >
                                    <Save size={16} /> {contactLoading ? 'Enregistrement...' : 'Enregistrer'}
                                </button>
                            </div>
                        </form>
                    </div>
                );
            default: return null;
        }
    };

    return (
        <div className="profile-modal-overlay" onClick={onClose}>
            <div className="profile-modal-container" onClick={e => e.stopPropagation()}>
                <div className="modal-sidebar">
                    <div className="sidebar-top-group">
                        <div className="sidebar-user-info">
                            <div className="avatar-main">
                                <img src={image || `https://ui-avatars.com/api/?name=${name}&background=700000&color=fff`} alt="User" />
                            </div>
                            <h3>{name || loggedUser.email.split('@')[0]}</h3>
                            <p>{loggedUser.email}</p>
                        </div>
                        <nav className="modal-nav">
                            <button className={activeTab === 'favoris' ? 'active' : ''} onMouseEnter={() => setActiveTab('favoris')}>
                                <Heart size={18} /> <span>Mes Favoris</span>
                            </button>
                            <button className={activeTab === 'missions' ? 'active' : ''} onMouseEnter={() => setActiveTab('missions')}>
                                <ClipboardList size={18} /> <span>Commandes</span>
                            </button>
                            <button className={activeTab === 'settings' ? 'active' : ''} onMouseEnter={() => setActiveTab('settings')}>
                                <Settings size={18} /> <span>Paramètres</span>
                            </button>
                            {loggedUser.role?.toLowerCase() === 'super admin' && (
                                <button className={activeTab === 'contact-settings' ? 'active' : ''} onMouseEnter={() => setActiveTab('contact-settings')}>
                                    <Phone size={18} /> <span>Contact</span>
                                </button>
                            )}
                        </nav>
                    </div>

                    <div className="sidebar-bottom-group">
                        {((loggedUser.role?.toLowerCase() === 'admin' || loggedUser.role?.toLowerCase() === 'super admin') && !hideAdminLink) && (
                            <Link to="/dashbordPage" className="modal-admin-link" onClick={onClose}>
                                <Shield size={18} /> <span>Retour Dashboard</span>
                            </Link>
                        )}
                        <button className="modal-logout-btn" onClick={handleLogout}>
                            <LogOut size={18} /> <span>Déconnexion</span>
                        </button>
                    </div>
                </div>
                <div className="modal-main-content">
                    <button className="modal-close-btn" onClick={onClose}><X size={24} /></button>
                    {renderContent()}
                </div>
            </div>

            {/* Order Detail Modal Overlay */}
            {selectedOrder && (
                <div className="mini-order-modal-overlay" onClick={() => setSelectedOrder(null)}>
                    <div className="mini-order-modal" onClick={e => e.stopPropagation()}>
                        <div className="mini-modal-header">
                            <h3>Commande #{selectedOrder.id}</h3>
                            <button onClick={() => setSelectedOrder(null)}><X size={20} /></button>
                        </div>
                        <div className="mini-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            <div className="detail-row"><span>Date</span> <span>{selectedOrder.date}</span></div>
                            <div className="detail-row"><span>Total</span> <strong>{selectedOrder.total} DH</strong></div>
                            <div className="detail-row"><span>Statut</span> <span className="status-badge">{selectedOrder.statut}</span></div>
                            <div className="order-items-list-mini">
                                <p className="section-p">Produits :</p>
                                {(selectedOrder.produits || selectedOrder.panier_Client)?.map((p, idx) => (
                                    <div key={idx} className="mini-product-item">
                                        <span>{(p.nom || p.nomClimatisseur)} x {p.quantite}</span>
                                        <span>{(p.prix * p.quantite) || p.prix_total} DH</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Image Cropper Modal */}
            {cropModalOpen && (
                <div className="cropper-overlay" onClick={() => setCropModalOpen(false)}>
                    <div className="cropper-card" onClick={e => e.stopPropagation()}>
                        <div className="cropper-header">
                            <h3>Ajuster la photo</h3>
                            <button onClick={() => setCropModalOpen(false)}><X size={20} /></button>
                        </div>
                        <div 
                            className="cropper-view-frame"
                            onMouseMove={handleFrameMouseMove}
                            onMouseUp={stopAction}
                            onMouseLeave={stopAction}
                            style={{ width: `${displaySize.w}px`, height: `${displaySize.h}px` }}
                        >
                            <img src={tempImage} alt="" className="cropper-bg-img" style={{ width: '100%', height: '100%' }} />
                            <div 
                                className="crop-frame-circle"
                                onMouseDown={handleFrameMouseDown}
                                style={{ 
                                    left: `${framePos.x}px`, 
                                    top: `${framePos.y}px`,
                                    width: `${frameSize}px`,
                                    height: `${frameSize}px`
                                }}
                            >
                                <div className="resize-handle br"></div>
                            </div>
                        </div>
                        <div className="cropper-controls">
                            <p className="hint-text">Faites glisser le cercle pour choisir le cadre</p>
                            <div className="cropper-btns">
                                <button className="btn-cancel-mini" onClick={() => setCropModalOpen(false)}>Annuler</button>
                                <button className="btn-save-mini" onClick={cropImageForPreview}>
                                    Appliquer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Custom Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
                    <div style={{ background: 'white', borderRadius: '24px', padding: '36px', maxWidth: '420px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.35)', border: '1px solid #eee' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(112,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <LogOut size={22} color="#700000" />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#000033', fontWeight: 800 }}>Déconnexion</h3>
                                <p style={{ margin: 0, fontSize: '0.82rem', color: '#888' }}>Vous allez fermer votre session.</p>
                            </div>
                        </div>
                        <p style={{ fontSize: '0.92rem', color: '#333', marginBottom: '28px', lineHeight: 1.6 }}>
                            Êtes-vous sûr de vouloir vous déconnecter de votre compte <strong>Jamas Lens</strong> ?
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowLogoutConfirm(false)} style={{ padding: '12px 24px', borderRadius: '12px', border: '1px solid #e2e8f0', background: 'transparent', color: '#475569', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', transition: 'all 0.2s' }} className="btn-cancel-logout">Annuler</button>
                            <button onClick={confirmLogoutAction} style={{ padding: '12px 24px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #700000, #a00000)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', boxShadow: '0 4px 12px rgba(112,0,0,0.2)', transition: 'all 0.2s' }} className="btn-confirm-logout">Se déconnecter</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
